"use client";

// WO-GF-001-P1B-S12 · RW2-9 — MARKETPLACE FILTER LAYER. Document-scoped hydrator (SiteLinkLayer /
// QuickViewLayer family) that makes the store's filter bar actually filter.
//
// FOUNDER RULING (c), 2026-07-22: "Filters: build now (F78 sizing is the spec baseline)... Real filtering
// against real listing data; if live's filters are no-op (F55), the founder ruling OVERRIDES live: make
// them WORK." So this is one of the few places the port deliberately exceeds live — recorded, not hidden.
//
// F78 sized two options: (i) client-side filtering over what the page already serves — contained, one layer,
// no new BE surface; (ii) a BE-backed filtered listings endpoint — faithful but a new cache/pagination
// contract, explicitly recommended only alongside the marketplace-depth work. This implements (i), the
// sized recommendation. Consequence, stated plainly: it filters the cards ALREADY on the page, so it cannot
// page in listings the server did not send. On these surfaces that is the whole set, so the distinction is
// currently invisible — but it is the reason this is not a substitute for (ii) later.
//
// NOTHING IS INVENTED — every predicate reads data the page already carries:
//   • product type  ← the checkbox's own `value="ps,<TYPE>"` matched against the card's
//                     `.cardHeading h3[title]` ("SNOW-ACTIVITY", "RENTALS", …). The vocabularies are
//                     literally identical, because both come from the same live data.
//   • company       ← the checkbox's `value="comp,<slug>"` matched against the card's supplier-logo
//                     basename, mapped to a supplier slug by the SAME BE-backed index the S11 quick-view
//                     join uses (logo basename == vendor.logoUrl basename). No name-guessing: the logo
//                     `whistler-the_adventures-logo.svg` resolves to `adventure-group` through the ported
//                     detail records, which a naive slug-from-filename would have got wrong.
//   • cost range    ← the captured range inputs, whose min/max are already the real price span of the cards.
//
// SEMANTICS: OR within a group, AND across groups; an untouched group constrains nothing. So the at-rest
// state (nothing checked, range at full span) shows every card — which is why this is PIXEL-NEUTRAL: the
// layer writes no style until the user actually moves a control.

import { useEffect } from "react";

export type FilterSupplier = { logo: string; slug: string };

const basename = (src: string | null) =>
  (src || "").split("/").pop()?.split("?")[0] || "";

// WO-GF-001-P1B-S36 card 2 · item 3 — F256/F257, THE PERKS SIDE OF THE PRODUCT/SERVICE PREDICATE.
// MEASURED FIRST on the running app (:3003) rather than assumed, because the two surfaces this one layer
// serves do not carry their category in the same place:
//
//   /deals-and-packages  ps options ["SNOW-ACTIVITY","SNOWMOBILE","VILLAGE-ACTIVITY","SNOWSHOE","RENTALS"]
//                        h3[title]  ["SNOW-ACTIVITY","SNOWMOBILE","VILLAGE-ACTIVITY","SNOWSHOE","RENTALS"]
//                        → identical vocabularies; the S12 exact-match predicate is correct here.
//   /snowclub-perks      ps options ["Activity","Accommodation","Rental","Rental, Retail"]
//                        h3[title]  ["5% OFF ","30% OFF","15% OFF","10% OFF 2 or more nights", …]
//                        span[title]["activity","Activity","Accommodation","Rentals","RENTALS",
//                                    "Rentals & Retail"]
//                        → the h3 title is the DISCOUNT. The category is the nested <span title>. The
//                          predicate was comparing "RENTAL" against "30% OFF", so the intersection was
//                          EMPTY and ticking any box hid all 10 cards. That is F256 exactly: the rental
//                          filter does not "sort out rentals", it returns nothing.
//
// Three independent mismatches, all measured, none guessed:
//   1. WRONG FIELD   — category lives in the h3's nested <span title>, not the h3's own title.
//   2. PLURAL/SEPARATOR — option "Rental" vs served "Rentals"/"RENTALS"; option "Rental, Retail" vs
//                      served "Rentals & Retail".
//   3. CASE DRIFT in the served data itself — "activity" and "Activity" on two different perks.
//
// THE FIX IS SYMMETRIC, which is the whole reason it invents nothing: `catTokens` is applied to the
// checkbox's own value AND to the card's own category with the same code, so it can only ever FAIL to
// match two strings — it can never manufacture a match between two genuinely different words the way a
// hand-written synonym table would. A compound value is read as the SET of categories it literally names
// ("Rentals & Retail" ⇒ RENTAL + RETAIL), so a supplier that is both is reachable from the Rental box —
// which is the founder's actual complaint. Match = non-empty intersection.
//
// NOT DONE, DELIBERATELY, AND REPORTED: the checkbox LIST is captured live markup and still offers
// "Rental, Retail" as if it were a category (it is one supplier's two categories glued together) while
// offering no "Retail" box of its own. Rewriting those labels would move the at-rest pixel baseline on a
// surface this card did not authorise a re-shoot for, and deriving the list from the cards would silently
// drop live options whenever a category has zero cards on the page. With set matching every option now
// selects exactly the cards that genuinely carry it, so nothing is unreachable; collapsing the compound
// option is a taste call and is routed, not guessed.
//
// SCOPE NOTE: `comp` (company) values are SLUGS — "ziptrek-ecotours" ends in an "s" that is part of the
// name, not a plural, and hyphens are significant. They are matched exactly, as before; only the `ps`
// group is tokenised.
const catTokens = (raw: string): string[] =>
  raw
    .split(/[,&/]|\band\b/i)
    .map((t) => t.replace(/[^a-z0-9]/gi, "").toUpperCase())
    .filter(Boolean)
    // trailing-plural only, and only on a token long enough that the strip cannot eat a short word
    .map((t) => (t.length > 3 && t.endsWith("S") ? t.slice(0, -1) : t));

// The category a card actually declares. Perk cards nest it in a <span> inside the heading; store cards
// put it in the heading's own title. Read the span when the card has one, otherwise the h3 — never both,
// so a store card cannot pick up a stray span from elsewhere in its heading.
const cardCategoryText = (card: HTMLElement): string => {
  const h3 = card.querySelector<HTMLElement>(".cardHeading h3");
  if (!h3) return "";
  const span = h3.querySelector<HTMLElement>("span[title]") ?? h3.querySelector<HTMLElement>("span");
  if (span) return span.getAttribute("title") || span.textContent || "";
  return h3.getAttribute("title") || h3.textContent || "";
};

export default function MarketplaceFilterLayer({
  suppliers,
}: {
  suppliers: FilterSupplier[];
}) {
  useEffect(() => {
    const bar = document.querySelector<HTMLElement>(
      '[class*="fitlerBarContainer"]',
    );
    const cards = Array.from(
      document.querySelectorAll<HTMLElement>(".cardBorder"),
    );
    if (!bar || cards.length === 0) return;
    // TS does not carry the null-narrowing of `bar` into the hoisted `function apply()` below, so take a
    // non-nullable alias here (drive-by typecheck fix — `tsc -p apps/root` was red on this file at 2e6fcc4).
    const barEl: HTMLElement = bar;

    const logoToSlug = new Map(suppliers.map((s) => [s.logo, s.slug]));

    // WO-GF-001-P1B-S13 · RW3-2 — FILTERED RESULTS MUST REFLOW.
    // Founder: survivors should pack to the first line, left-to-right, with no gaps; the S12 filter left
    // them in their original grid spots. ROOT CAUSE, measured on the served store page: the captured grid
    // is `section.centerColumn { display:flex }` › a per-card `<section>` wrapper › `.cardContainer` ›
    // `.cardBorder`. S12 hid `.cardBorder`, which empties the card but leaves its 275px flex CELL standing
    // — hence the holes. Hiding the CELL (the flex/grid container's own child) lets flex re-pack.
    // NO LIVE REFERENCE EXISTS for this: live's filters are no-ops (F55), so per the WO this is a ruled,
    // documented choice — "pack left-to-right, no gaps" = plain source order, which is what removing the
    // cell from flow gives. Nothing is reordered and no style is written until a control is used, so the
    // at-rest pixel gate is untouched by construction.
    const layoutCellOf = (card: HTMLElement): HTMLElement => {
      let el: HTMLElement = card;
      for (let i = 0; i < 6; i++) {
        const parent = el.parentElement;
        if (!parent) break;
        const disp = getComputedStyle(parent).display;
        if (disp === "flex" || disp === "grid" || disp === "inline-flex") return el;
        el = parent;
      }
      return card; // no flow container found → fall back to the S12 behaviour rather than guess
    };

    // Index every card ONCE off its own markup.
    const indexed = cards.map((card) => {
      const typeTokens = catTokens(cardCategoryText(card));
      const logo = basename(
        card.querySelector<HTMLImageElement>("img.supplierLogo")?.getAttribute("src") ?? null,
      );
      // the headline (member) price; falls back to the guest price when a card shows only one
      const num = (sel: string) => {
        const t = card.querySelector(sel)?.textContent || "";
        const m = /(\d+(?:\.\d+)?)/.exec(t.replace(/,/g, ""));
        return m ? Number(m[1]) : null;
      };
      const member = num(".leftPrice strong");
      const guest = num(".rightPrice del");
      return {
        card,
        cell: layoutCellOf(card),
        typeTokens,
        slug: logoToSlug.get(logo) ?? null,
        member,
        guest,
      };
    });

    const checkboxes = Array.from(
      bar.querySelectorAll<HTMLInputElement>('input[type="checkbox"][value]'),
    );
    const ranges = Array.from(
      bar.querySelectorAll<HTMLInputElement>('input[type="range"]'),
    );
    const priceTexts = Array.from(
      bar.querySelectorAll<HTMLInputElement>('input[type="text"]'),
    );

    const groupOf = (cb: HTMLInputElement) =>
      (cb.getAttribute("value") || "").split(",")[0] || "";
    const valueOf = (cb: HTMLInputElement) =>
      (cb.getAttribute("value") || "").split(",").slice(1).join(",");

    function apply() {
      const picked = new Map<string, Set<string>>();
      for (const cb of checkboxes) {
        if (!cb.checked) continue;
        const g = groupOf(cb);
        if (!picked.has(g)) picked.set(g, new Set());
        // `ps` is a CATEGORY and is read as the set of categories the option literally names; every other
        // group (today: `comp`, whose values are slugs) keeps the exact match it has always had.
        if (g === "ps") for (const t of catTokens(valueOf(cb))) picked.get(g)!.add(t);
        else picked.get(g)!.add(valueOf(cb).trim().toUpperCase());
      }
      const nums = ranges
        .map((r) => Number(r.value))
        .filter((n) => Number.isFinite(n));
      const lo = nums.length ? Math.min(...nums) : -Infinity;
      const hi = nums.length ? Math.max(...nums) : Infinity;

      let shown = 0;
      for (const it of indexed) {
        const byType =
          !picked.has("ps") ||
          it.typeTokens.some((t) => picked.get("ps")!.has(t));
        const bySlug =
          !picked.has("comp") ||
          (!!it.slug && picked.get("comp")!.has(it.slug.toUpperCase()));
        // a card passes the range if EITHER price it actually shows falls inside it
        const prices = [it.member, it.guest].filter(
          (n): n is number => typeof n === "number",
        );
        const byPrice =
          prices.length === 0 || prices.some((p) => p >= lo && p <= hi);
        const ok = byType && bySlug && byPrice;
        // RW3-2 — hide the LAYOUT CELL, not the card, so survivors reflow (see layoutCellOf).
        it.cell.style.display = ok ? "" : "none";
        if (ok) shown++;
      }
      barEl.setAttribute("data-filter-shown", String(shown));
      barEl.setAttribute("data-filter-total", String(indexed.length));
    }

    // keep the captured "$min / $max" readouts honest as the sliders move (live shows them as text inputs)
    function syncPriceText() {
      const nums = ranges.map((r) => Number(r.value)).filter(Number.isFinite);
      if (nums.length < 2 || priceTexts.length < 2) return;
      const lo = Math.min(...nums);
      const hi = Math.max(...nums);
      if (priceTexts[0]) priceTexts[0].value = `$${lo}`;
      if (priceTexts[1]) priceTexts[1].value = `$${hi}`;
    }

    const onChange = () => {
      syncPriceText();
      apply();
    };
    for (const cb of checkboxes) cb.addEventListener("change", onChange);
    for (const r of ranges) {
      r.addEventListener("input", onChange);
      r.addEventListener("change", onChange);
    }

    // "Clear Filters" — live renders it as a <span>; it must actually reset.
    const clear = Array.from(
      bar.querySelectorAll<HTMLElement>("span"),
    ).find((s) => /clear\s*filters/i.test(s.textContent || ""));
    const onClear = (e: Event) => {
      e.preventDefault();
      for (const cb of checkboxes) cb.checked = false;
      for (const r of ranges) r.value = r.getAttribute("value") ?? r.value;
      syncPriceText();
      apply();
    };
    if (clear) {
      clear.setAttribute("data-behavior", "filter-clear");
      clear.style.cursor = "pointer";
      clear.addEventListener("click", onClear);
    }

    // "Add Filters" is live's MOBILE apply button (class `mobileFilterBtn`); on desktop the controls apply
    // immediately, so it simply re-applies rather than pretending to be a second filter mechanism.
    const addBtn = Array.from(
      bar.querySelectorAll<HTMLElement>("button"),
    ).find((b) => /add\s*filters/i.test(b.textContent || ""));
    const onAdd = (e: Event) => {
      e.preventDefault();
      apply();
    };
    if (addBtn) {
      addBtn.setAttribute("data-behavior", "filter-apply");
      addBtn.addEventListener("click", onAdd);
    }

    bar.setAttribute("data-behavior", "marketplace-filters");
    bar.setAttribute("data-filter-total", String(indexed.length));

    return () => {
      for (const cb of checkboxes) cb.removeEventListener("change", onChange);
      for (const r of ranges) {
        r.removeEventListener("input", onChange);
        r.removeEventListener("change", onChange);
      }
      clear?.removeEventListener("click", onClear);
      addBtn?.removeEventListener("click", onAdd);
      for (const it of indexed) it.card.style.display = "";
    };
  }, [suppliers]);

  return null;
}
